// ══════════════════════════════════════════════════════════════════════════════
// PERSISTENT STORE — localStorage wrapper with JSON serialisation + ENCRYPTION
// SEC-03: AES-256-GCM encryption for patient-identifiable data at rest
// ══════════════════════════════════════════════════════════════════════════════

import { Encryption } from './Security.js';

const PREFIX = 'sykes-';

// FOLLOWUP-#2 FIX: callers can pass keys with OR without the 'sykes-' prefix.
// Both forms resolve to the same canonical localStorage key. Historically some
// call sites passed 'sykes-X' to save() which then prepended PREFIX again,
// resulting in localStorage entries at 'sykes-sykes-X' (functional but ugly,
// inconsistent, and bypassing the SENSITIVE_KEYS lookup style).
function lsKey(key) {
  return key.startsWith(PREFIX) ? key : PREFIX + key;
}

// Keys that contain patient-identifiable or clinical data → MUST be encrypted.
// Stored without the 'sykes-' prefix; isSensitive() handles either form.
const SENSITIVE_KEYS = new Set([
  'admin_patients', 'admin_clinical_sessions', 'admin_patient_forms',
  'ana_patients', 'ana_patient_forms',
  'clinic_registered_patients', 'clinic_patient_forms',
  'anaesthetist-records', 'anaesthetist-review-queue',
  'booking-pipeline', 'admin-notifications',
  'audit-log',
  // BUG-#2 ADDITIONS: previously plaintext, now flagged for encryption
  'patient-records', 'patient-index',
  'completed-sessions',
  'host-clinics',
  // FOLLOWUP-#6: NOTE the absence of 'ana_pushed_sessions' here.
  // It was briefly added in FOLLOWUP-#5 but reverted because the
  // Encryption passphrase derives from the JWT token and so changes on
  // every login. That means encrypted data cannot be decrypted in a
  // subsequent session — pushedRef would always restore as empty {},
  // and the auto-push effect would re-push every previously-completed
  // session on every login, spamming admin with notifications.
  // The marginal PII gain (CRNs in this value's keys are already exposed
  // via the sykes-pt:CRN sharded key names anyway) does not justify that
  // breakage. Kept plaintext until a stable-across-sessions encryption
  // key strategy is in place, or pushedRef is migrated to server-side
  // state via APIStore.loadCompletedSessions.
]);

// Prefix patterns for sharded sensitive data (sykes-pt:CRN, sykes-forms:CRN)
const SENSITIVE_PREFIXES = ['sykes-pt:', 'sykes-forms:'];

function isSensitive(key) {
  // Accept either prefixed or unprefixed form. Strip the prefix for the
  // SENSITIVE_KEYS lookup since the set stores unprefixed names only.
  const stripped = key.startsWith(PREFIX) ? key.slice(PREFIX.length) : key;
  if (SENSITIVE_KEYS.has(stripped)) return true;
  // For sharded prefixes (sykes-pt:, sykes-forms:), check the canonical full key.
  const full = lsKey(key);
  for (const p of SENSITIVE_PREFIXES) {
    if (full.startsWith(p)) return true;
  }
  return false;
}

// ── Serialisation cache ─────────────────────────────────────────────────────
// Cache is keyed by the canonical (full prefixed) key so save('X') and
// save('sykes-X') share a cache entry and the change-detection optimisation
// works regardless of which form the caller chose.
const _cache = new Map();
const _timers = new Map();

export const PersistentStore = {
  load(key) {
    const k = lsKey(key);
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return null;
      // Encrypted data needs async path
      if (raw.startsWith('ENC:')) {
        const cached = _cache.get(k);
        if (cached) return JSON.parse(cached);
        return null; // use loadAsync for first access of encrypted data
      }
      if (!_cache.has(k)) _cache.set(k, raw);
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[PersistentStore] load error:', key);
      return null;
    }
  },

  /** Async load — handles decryption of sensitive data (SEC-03) */
  async loadAsync(key) {
    const k = lsKey(key);
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return null;

      // Encrypted-data path. The two failure modes here are *expected*, not
      // bugs: (a) Encryption isn't initialised yet because the user hasn't
      // logged in (typical pre-login load); (b) Encryption IS initialised
      // but the JWT-derived passphrase doesn't match the one used to write
      // this ciphertext (typical when stale data from a previous session
      // survives because the browser was closed without a clean logout).
      // In both cases callers expect null — we return it silently rather
      // than warning on every page load.
      if (raw.startsWith('ENC:')) {
        if (!Encryption.isEnabled()) return null;
        const decrypted = await Encryption.decrypt(raw);
        // Encryption.decrypt returns the ciphertext unchanged when it can't
        // decrypt (wrong key, corrupt blob, etc.). Treat that as null too.
        if (!decrypted || decrypted.startsWith('ENC:')) return null;
        _cache.set(k, decrypted);
        return JSON.parse(decrypted);
      }

      // Plaintext path — a JSON.parse failure here is a genuine error
      // (corrupt data) worth warning about.
      _cache.set(k, raw);
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[PersistentStore] loadAsync error:', key, e?.message || e);
      return null;
    }
  },

  /** Immediate save — encrypts sensitive keys if encryption enabled */
  /** Uses requestIdleCallback to avoid blocking the main thread */
  save(key, value) {
    const k = lsKey(key);
    try {
      const json = JSON.stringify(value);
      if (_cache.get(k) === json) return;
      _cache.set(k, json); // Update cache immediately (sync)
      const writeToStorage = () => {
        try {
          if (isSensitive(k) && Encryption.isEnabled()) {
            Encryption.encrypt(json).then(encrypted => {
              localStorage.setItem(k, encrypted);
            }).catch(() => {
              localStorage.setItem(k, json);
            });
          } else {
            localStorage.setItem(k, json);
          }
        } catch (e) {
          console.warn('[PersistentStore] write error:', key);
        }
      };
      // Defer the actual localStorage write to idle time
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(writeToStorage, { timeout: 1000 });
      } else {
        setTimeout(writeToStorage, 0);
      }
    } catch (e) {
      console.warn('[PersistentStore] save error:', key);
    }
  },

  /** Async save with await for encryption */
  async saveAsync(key, value) {
    const k = lsKey(key);
    try {
      const json = JSON.stringify(value);
      if (_cache.get(k) === json) return;
      if (isSensitive(k) && Encryption.isEnabled()) {
        const encrypted = await Encryption.encrypt(json);
        localStorage.setItem(k, encrypted);
      } else {
        localStorage.setItem(k, json);
      }
      _cache.set(k, json);
    } catch (e) {
      console.warn('[PersistentStore] saveAsync error:', key);
    }
  },

  /** Debounced save — batches rapid successive writes (default 300ms) */
  saveLater(key, value, delay = 300) {
    const k = lsKey(key);
    if (_timers.has(k)) clearTimeout(_timers.get(k));
    _timers.set(k, setTimeout(() => {
      _timers.delete(k);
      PersistentStore.save(k, value);
    }, delay));
  },

  loadIfChanged(key) {
    const k = lsKey(key);
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return null;
      if (raw.startsWith('ENC:')) return null;
      if (_cache.get(k) === raw) return null;
      _cache.set(k, raw);
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[PersistentStore] loadIfChanged error:', key);
      return null;
    }
  },

  remove(key) {
    const k = lsKey(key);
    try {
      localStorage.removeItem(k);
      _cache.delete(k);
      if (_timers.has(k)) { clearTimeout(_timers.get(k)); _timers.delete(k); }
    } catch (e) {
      console.warn('[PersistentStore] remove error:', key);
    }
  },

  keys() {
    const result = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) result.push(k.slice(PREFIX.length));
    }
    return result;
  },

  /**
   * BUG-#2 FIX: purge all PII from local storage.
   * Wipes every `sykes-*` key from localStorage, clears sessionStorage entirely,
   * and resets in-memory caches. Call this on logout, auto-logout, and any
   * "end of authenticated session" boundary to prevent next-user data leakage.
   * Returns the number of localStorage keys removed.
   */
  purgeAllPII() {
    let removed = 0;
    try {
      // Collect first (mutating during iteration breaks indexing)
      const toDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) toDelete.push(k);
      }
      for (const k of toDelete) {
        try { localStorage.removeItem(k); removed++; } catch(_) {}
      }
    } catch (e) { console.warn('[PersistentStore] purge localStorage error:', e); }
    try { sessionStorage.clear(); } catch(_) {}
    try {
      _cache.clear();
      for (const t of _timers.values()) clearTimeout(t);
      _timers.clear();
    } catch(_) {}
    return removed;
  },

  /**
   * FOLLOWUP-#2 FIX: one-time migration of legacy double-prefixed localStorage
   * keys. Some call sites used to pass 'sykes-X' to save(), which then prepended
   * the PREFIX again — producing entries at 'sykes-sykes-X'. With lsKey() in
   * place those callers now resolve to the canonical 'sykes-X' key, so the
   * legacy entries would otherwise be orphaned. This method copies their values
   * to the canonical key (if the canonical key is empty) and removes the old
   * double-prefixed entries.
   *
   * Idempotent: calling repeatedly is safe.
   */
  migrateDoublePrefixedKeys() {
    if (typeof localStorage === 'undefined') return 0;
    let migrated = 0;
    try {
      const oldKeys = [];
      const dblPrefix = PREFIX + PREFIX; // 'sykes-sykes-'
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(dblPrefix)) oldKeys.push(k);
      }
      for (const oldKey of oldKeys) {
        const newKey = oldKey.slice(PREFIX.length); // strip one PREFIX → 'sykes-X'
        try {
          const value = localStorage.getItem(oldKey);
          // Only copy if the canonical key is empty — otherwise prefer existing
          // (newer) data at the canonical location.
          if (value !== null && localStorage.getItem(newKey) === null) {
            localStorage.setItem(newKey, value);
          }
          localStorage.removeItem(oldKey);
          migrated++;
        } catch(_) {}
      }
      if (migrated > 0) {
        console.log(`[PersistentStore] Migrated ${migrated} double-prefixed key(s) → canonical form`);
      }
    } catch (e) {
      console.warn('[PersistentStore] migrate error:', e);
    }
    return migrated;
  }
};

// Run the double-prefix migration once when this module is first imported.
// Idempotent and cheap when no double-prefixed entries exist.
(() => {
  try { PersistentStore.migrateDoublePrefixedKeys(); } catch(_) {}
})();

// FOLLOWUP-#3 (26 Apr 2026): pushedRef has migrated to server-side state
// (AnaesthetistPortal hydrates from APIStore.loadCompletedSessions). The
// sykes-ana_pushed_sessions key is no longer read or written anywhere.
// Remove it on module import — supersedes the FOLLOWUP-#6 cleanup that
// only targeted ENC:-prefixed orphans. Idempotent and a no-op once the
// key is gone.
(() => {
  try {
    const k = PREFIX + 'ana_pushed_sessions';
    if (localStorage.getItem(k) !== null) {
      localStorage.removeItem(k);
      console.log('[PersistentStore] Removed legacy ana_pushed_sessions (FOLLOWUP-#3)');
    }
  } catch(_) {}
})();
